import { AfterViewInit, ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { mergeConfig } from '../ui-editor/prism/prism-config.util';
import { ChartId, CHART_LABELS, chartOfForm } from '../ui-editor/prism/prism-chart.util';
import { PrismPlotComponent } from '../../common-components/dynamic-components/prism/prism-plot.component';
import { ChartConfig, ChartStyle, ChartTheme, PaletteId, PresetId, WEIGHT_LABELS } from '../ui-editor/prism/prism-style.types';
import { PALETTES, PALETTE_LABELS } from '../ui-editor/prism/prism-palettes';
import { Knob, knobsFor, houseDefault } from '../ui-editor/prism/prism-knobs';
import { PRESETS, PRESET_LABELS } from '../ui-editor/prism/prism-presets';
import { PREVIEW_FIXTURE } from '../ui-editor/prism/prism-preview.fixture';
import { ChartThemeService, EMPTY_THEME } from '../../services/chart-theme.service';
import { registerScreenActions, registerScreenPanels } from '../../studio-nav/screen-panels.service';
import type { ScreenActionAnswer } from '../../services/mcp/actions/fast-lane';

/**
 * Where a builder decides what their charts look like.
 *
 * ─── BUILDER-LEVEL, NOT PROJECT-LEVEL ─────────────────────────────────────────────────────────────
 * This page edits `builderPrefs/{uid}` — the PERSON's preference, which is why it is reached from the
 * project list rather than from inside a project, and why Back returns there. Nothing here is scoped
 * to a client, and selecting one is neither required nor performed.
 *
 * A project with no theme of its own resolves against what is saved here (see `ChartThemeService`),
 * so this screen is immediately visible on existing work without a migration.
 *
 * ─── THE PREVIEW IS THE REAL RENDERER ─────────────────────────────────────────────────────────────
 * Not a mockup and not a screenshot: every card runs `resolveChartStyle` → `renderPrism` → Chart.js,
 * the same path a page uses. So a preference that survives this screen survives a real dashboard, and
 * a clamp that fires here fires there. The data is a fixed fixture, which is what lets the page
 * render with no client selected, no query, and nothing to fail.
 */
@Component({
  selector: 'app-chart-style',
  imports: [CommonModule, FormsModule, PrismPlotComponent],
  templateUrl: './chart-style.component.html',
})
export class ChartStyleComponent implements AfterViewInit {
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private themes = inject(ChartThemeService);
  private panels = registerScreenPanels({
    screen: 'chart-style',
    loading: () => this.lastStored === null,
    here: () => ({}),
    select: () => false,
    holds: () => ({
      presets: this.presets,
      knobs: this.knobs.map((k) => ({
        key: k.key,
        label: k.label,
        charts: k.charts,
        options: k.options.map((o) => o.value),
        ...(k.slider ? { slider: { min: k.slider.min, max: k.slider.max } } : {}),
        ...(k.list ? { list: true } : {}),
      })),
      scope: this.scope,
      theme: this.theme,
    }),
    unsaved: () => (this.hasUnsavedStyle() ? { count: 1, name: 'Chart style' } : null),
  });
  private offers = registerScreenActions({ 'set-chart-style': (values) => this.setStyleFor(values) });

  theme: ChartTheme = structuredClone(EMPTY_THEME);
  readonly fixture = PREVIEW_FIXTURE;
  readonly presets = Object.keys(PRESETS) as PresetId[];
  readonly presetLabel = PRESET_LABELS;

  /** `''` = the global layer. Otherwise the CHART whose `perChart` entry is being edited. */
  scope: '' | ChartId = '';
  /**
   * ⚠ DEDUPED BY CHART, WHICH IS THE KEY IT WRITES TO. `perChart` is keyed by chart id, so the scope
   * list has to be chart ids too. Deduping the fixture's FORMS instead left "Only sparkline", "Only
   * line" and "Only area" as three entries that all edited `perChart.line` — and bar/hbar,
   * donut/gauge and stacked/hstacked the same — so a builder could set a knob under one heading and
   * find it set under another. The fixture carries those variants deliberately (a line with a legend
   * and crossing series, a horizontal bar); what it must not do is imply they are separately
   * configurable.
   */
  readonly scopes: ChartId[] = [...new Set(PREVIEW_FIXTURE.map(f => chartOfForm(f.form)))];
  /** The scope select reads the chart's own label, so the heading names what it writes to. */
  readonly chartLabels = CHART_LABELS;

  saving = false;
  saved = false;
  error = '';
  /** The look as it was last read or written; null until the stored one has arrived. */
  private lastStored: string | null = null;
  /** Notes per preview index, so a clamp is visible here instead of only in the console. */


  /**
   * ⚠ FROM THE SHARED CATALOGUE, not a local list — one list, two screens, so a knob cannot exist on
   * the Chart tab and not here. `knobsFor` returns EVERY knob today: there is no longer a knob this
   * screen must be kept away from (`form` went with the family model, and the chart itself is chosen
   * per chart node, never project-wide). The scope select below is what narrows them.
   */
  readonly knobs: Knob[] = knobsFor('theme');

  async ngAfterViewInit(): Promise<void> {
    this.theme = structuredClone(await this.themes.loadBuilderPrefs());
    this.lastStored = JSON.stringify(this.theme);
    // ⚠ NO CHART.JS HERE ANY MORE. Each <prism-plot> loads it lazily through the shared loader and
    // draws itself; this screen's job is the knobs and the scope being edited.
    this.bump();
  }



  /**
   * The merged config for one preview card.
   *
   * ⚠ CACHED AGAINST A VERSION. `mergeConfig` is pure and cheap, but a template-called method that
   * returns a fresh object every change-detection cycle hands an OnPush child a new reference every
   * time Angular looks at it — which re-renders it forever. The version moves only when a knob does.
   *
   * ⚠ AND IT MUST CHANGE WHEN A KNOB CHANGES. The scope is mutated IN PLACE by `set`/`clear`, and a
   * mutated object is not a change to an OnPush child: without the bump the previews would silently
   * stop responding to clicks, with no error anywhere.
   */
  private configCache = new Map<number, { v: number; config: ChartConfig }>();
  private configVersion = 0;

  configFor(i: number): ChartConfig {
    const hit = this.configCache.get(i);
    if (hit && hit.v === this.configVersion) return hit.config;
    const entry = this.fixture[i];
    const config: any = mergeConfig(this.theme, entry.form, {});
    // The requested form rides IN the config, so the plot takes one input describing the whole look.
    config.form = entry.form;
    // Not a knob — a fact about the data that gates stacking. Without it the preview cannot show a
    // refusal, and a builder would meet the additivity gate for the first time on a real page.
    config.measure = entry.measure;
    this.configCache.set(i, { v: this.configVersion, config });
    return config;
  }

  /** Invalidate every card's config. Called wherever the scope is edited. */
  private bump(): void { this.configVersion++; this.cdr.markForCheck(); }

  // ── the knob layer being edited ─────────────────────────────────────────────────────────────────

  /** The layer a knob writes to: the global one, or this form's entry. */
  private layerFor(knob: Knob): Partial<ChartStyle> {
    if (knob.charts === 'all' || !this.scope) return this.theme.global;
    return (this.theme.perChart[this.scope] ??= {});
  }

  current(knob: Knob): any {
    const layer = this.layerFor(knob);
    if (layer[knob.key] !== undefined) return layer[knob.key];
    // Fall through to the preset, then the house default, so the highlighted button always shows
    // what would actually be drawn rather than "nothing selected".
    const preset = PRESETS[this.theme.preset] ?? {};
    if (preset[knob.key] !== undefined) return preset[knob.key];
    return houseDefault(knob.key);
  }

  set(knob: Knob, value: any): void {
    (this.layerFor(knob) as any)[knob.key] = value;
    this.saved = false;
    this.bump();
  }

  /** Hand a knob back to the layer above. The visible form of the sparse-override model. */
  clear(knob: Knob): void {
    delete (this.layerFor(knob) as any)[knob.key];
    this.saved = false;
    this.bump();
  }

  isSet(knob: Knob): boolean {
    return this.layerFor(knob)[knob.key] !== undefined;
  }

  choosePreset(id: PresetId): void {
    this.theme.preset = id;
    this.saved = false;
    this.bump();
  }

  /** Knobs shown for the current scope: everything globally, only the per-chart ones inside a chart. */
  visibleKnobs(): Knob[] {
    return this.scope ? this.knobs.filter(k => k.charts !== 'all') : this.knobs;
  }

  onScopeChange(): void { this.bump(); }

  /**
   * A slider reports its value as a STRING. Coerce at the boundary, or the resolver's clamp receives
   * "75" and `Number.isFinite` passes on a value that then fails every arithmetic comparison
   * downstream.
   */
  setSlider(knob: Knob, raw: string | number): void {
    this.set(knob, Number(raw));
  }

  /** The nearest named position, so the slider can say "Regular" rather than only "50". */
  sliderLabel(knob: Knob): string {
    const v = Number(this.current(knob));
    const marks = knob.slider?.marks ?? [];
    const exact = marks.find(m => m.at === v);
    return exact ? exact.label : String(v);
  }

  // ── drawing ─────────────────────────────────────────────────────────────────────────────────────



  // ── persistence ─────────────────────────────────────────────────────────────────────────────────

  async save(): Promise<void> {
    this.saving = true; this.error = '';
    try {
      await this.themes.saveBuilderPrefs(this.theme);
      this.saved = true;
      this.lastStored = JSON.stringify(this.theme);
    } catch (e: any) {
      this.error = e?.message ?? 'could not save';
    } finally {
      this.saving = false;
    }
  }

  reset(): void {
    this.theme = structuredClone(EMPTY_THEME);
    this.saved = false;
    this.bump();
  }

  back(): void { void this.router.navigate(['/dashboard/client-list']); }

  // ── the same knobs and the same save, for do_action ─────────────────────────────────────────────

  private hasUnsavedStyle(): boolean {
    return this.lastStored !== null && JSON.stringify(this.theme) !== this.lastStored;
  }

  /** The screen's buttons can only offer what's listed, so a value from outside is held to those lists here. */
  private knobValueProblem(knob: Knob, value: unknown): string | null {
    if (value === null) return null;
    if (knob.list) return Array.isArray(value) ? null : `${knob.key} takes a list.`;
    if (knob.slider) {
      const ok = typeof value === 'number' && value >= knob.slider.min && value <= knob.slider.max;
      return ok ? null : `${knob.key} takes a number from ${knob.slider.min} to ${knob.slider.max}.`;
    }
    const offered = knob.options.map((o) => o.value);
    return offered.includes(value) ? null : `${knob.key} takes one of: ${offered.join(', ')}.`;
  }

  private layerProblem(layer: unknown, allowed: Knob[], where: string): string | null {
    if (!layer || typeof layer !== 'object') return `${where} takes knobs by name.`;
    for (const [key, value] of Object.entries(layer)) {
      const knob = allowed.find((k) => k.key === key);
      if (!knob) return `${where} has no knob "${key}". It has: ${allowed.map((k) => k.key).join(', ')}.`;
      const problem = this.knobValueProblem(knob, value);
      if (problem) return problem;
    }
    return null;
  }

  private themeProblem(theme: Record<string, unknown>): string | null {
    const stray = Object.keys(theme).find((key) => !['preset', 'global', 'perChart'].includes(key));
    if (stray) return `theme has no "${stray}". It takes preset, global and perChart.`;
    if (theme['preset'] !== undefined && !this.presets.includes(theme['preset'] as PresetId)) return `preset takes one of: ${this.presets.join(', ')}.`;
    if (theme['global'] !== undefined) {
      const problem = this.layerProblem(theme['global'], this.knobs, 'global');
      if (problem) return problem;
    }
    if (theme['perChart'] !== undefined) {
      if (!theme['perChart'] || typeof theme['perChart'] !== 'object') return 'perChart takes knobs by chart.';
      for (const [chart, layer] of Object.entries(theme['perChart'])) {
        if (!this.scopes.includes(chart as ChartId)) return `perChart has no chart "${chart}". It has: ${this.scopes.join(', ')}.`;
        const problem = this.layerProblem(layer, this.knobs.filter((k) => k.charts !== 'all'), `perChart.${chart}`);
        if (problem) return problem;
      }
    }
    return null;
  }

  private applyLayer(layer: Record<string, unknown>, scope: '' | ChartId): void {
    this.scope = scope;
    for (const [key, value] of Object.entries(layer)) {
      const knob = this.knobs.find((k) => k.key === key)!;
      if (value === null) this.clear(knob);
      else this.set(knob, value);
    }
  }

  private async setStyleFor(values: Record<string, unknown>): Promise<ScreenActionAnswer> {
    if (this.lastStored === null) return { refused: 'Chart style is still loading the stored look. Nothing was changed; try again in a moment.' };
    if (this.saving) return { refused: 'Chart style is saving. Nothing was changed; try again in a moment.' };
    if (this.hasUnsavedStyle()) return { refused: 'Chart style has unsaved changes; ask the builder to save or reset them first. Nothing was changed.' };

    const theme = values['theme'] as Record<string, unknown> | undefined;
    if (!theme) return { refused: 'Chart style needs a theme, with any of preset, global and perChart. Nothing was changed.' };
    const scope = (values['scope'] ?? this.scope) as '' | ChartId;
    if (scope !== '' && !this.scopes.includes(scope)) return { refused: `scope takes "" for all charts, or one of: ${this.scopes.join(', ')}. Nothing was changed.` };
    const problem = this.themeProblem(theme);
    if (problem) return { refused: `${problem} Nothing was changed.` };

    const before = { theme: structuredClone(this.theme), scope: this.scope };
    if (theme['preset'] !== undefined) this.choosePreset(theme['preset'] as PresetId);
    if (theme['global']) this.applyLayer(theme['global'] as Record<string, unknown>, '');
    for (const [chart, layer] of Object.entries((theme['perChart'] ?? {}) as Record<string, Record<string, unknown>>)) {
      this.applyLayer(layer, chart as ChartId);
    }
    this.scope = scope;
    await this.save();

    if (this.error) {
      this.theme = before.theme;
      this.scope = before.scope;
      this.bump();
      return { refused: `Chart style wasn't saved: ${this.error}` };
    }
    return { done: 'Saved the chart style.', result: { saved: structuredClone(this.theme), scope: this.scope } };
  }
}


